import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { useSearchParams } from "react-router";
import { contractNameProblem, type ContractRole, type CustomVariableDef } from "@talentkontor/domain";
import { api, ApiError } from "../../data";
import { toggleTheme } from "../../lib/theme";
import { CreatorOfficeLogo } from "../../components/shell/Logo";
import { PLATFORM_NAME } from "../../lib/marke";
import { setUiLang } from "../../lib/sprache";
import { Toasts } from "../../components/Toast";
import { WEBSITE_URL } from "../../app/nav";
import { VertragPapier, type SignatureMark } from "../agentur/vertraege/Papier";
import { SignaturePad, type SignatureValue } from "../agentur/vertraege/Unterschrift";
import { clientInfo } from "../agentur/vertraege/daten";
import { contractNumberLabel } from "../agentur/vertraege/modell";
import { ausfertigungFileName, ausfertigungPdf, protokollFileName, saveFile, unterschriftenprotokollPdf } from "../agentur/vertraege/ausfertigung";
import { paperDates } from "../agentur/vertraege/protokoll";
import { DokumentAnsicht, useDokument } from "../agentur/vertraege/DokumentAnsicht";
import { confirmReadPublic, documentError } from "../agentur/vertraege/dokument";

/**
 * Vertrag per persönlichem Link unterschreiben (13_oeffentlich/signieren.html), ohne Konto. Oberfläche in der Sprache des
 * Unterzeichners (Befund 8), Sie-Form für Marke und gesetzliche Vertretung, Du-Form für Creator. Nach der Unterschrift
 * bleibt der Link ein Lesezugang; die Ausfertigung kommt mit eigenem Leselink (?l=…, Befund 7). Angehaltene, pausierte
 * und aufgehobene Verträge sagen das klar (Befund 5, 11, 13). Fußzeile mit Impressum und Datenschutz (Befund 22).
 * Bedienbarkeits-Prüfung (Inhaber 02.11.2026): Nach „Ablehnen“ bleibt der Link eine Lesesicht in der Sprache des Vertrags,
 * mit Agentur, Datum, Grund und dem Vertrag (CP-025); alle Bausteine der Seite sprechen dieselbe Sprache (CP-026).
 */

type Lang = "de" | "en";
type Form = "sie" | "du";

interface PublicSigner {
  role: ContractRole; slot: number; capacity: "party" | "legal_guardian"; name: string; signed: boolean; signed_at: string | null;
  signing_name: string | null; signing_position: string | null; signing_authority: string | null; signature_data: string | null; declined: boolean;
  paper?: boolean;
}
export interface PublicView {
  mode: "sign" | "read";
  /** PDF-Vorlage der Agentur für Verträge (0456), nur Gestaltung */
  layout?: unknown;
  state: "open" | "stopped" | "paused" | "completed" | "void";
  contract_id: string;
  title: string;
  language: Lang;
  status: string;
  contract_number: number | null;
  contract_number_label: string | null;
  sent_at: string | null;
  completed_at: string | null;
  /** 0059: Aufhebung und Kündigung (die Ausfertigung bleibt lesbar), Zeitzone und Anrede der Agentur */
  voided_at?: string | null;
  void_kind?: "withdrawn" | "error" | "mutual" | "superseded" | null;
  void_reason?: string | null;
  terminated_on?: string | null;
  timezone?: string | null;
  address_form?: "du" | "sie";
  /** Paket 2a (0571): Art des Kontos; „creator“ = selbständiger Creator ohne Agentur, die Partei „agency“ heißt dann „Creator“ */
  account_kind?: "agency" | "creator" | null;
  agency: {
    name: string | null; logo_path: string | null; brand_color: string | null; legal_name: string | null; address: string | null; representative: string | null;
    register: string | null; vat_id: string | null; email: string | null; privacy_email: string | null; website: string | null; privacy_url: string | null; imprint_url: string | null;
  };
  role: ContractRole;
  expires_at: string | null;
  read_until?: string | null;
  signer: { name: string; email: string; signed_at: string | null; capacity: "party" | "legal_guardian"; slot: number; ui_language: Lang; position: string | null };
  html: string;
  annex_html: string | null;
  fillable: CustomVariableDef[];
  values: Record<string, string>;
  locked: boolean;
  signers: PublicSigner[];
  document_sha256: string | null;
  view_sha256: string | null;
  final_sha256: string | null;
  /** 0117 (Prüfung 0–2, Befund 6): ohne Sicht auf den vertraulichen Teil nur Prüfwerte des Hauptteils statt des ganzen Vertrags */
  part_sha256?: string | null;
  part_final_sha256?: string | null;
  protocol: { event: string; at: string; role: ContractRole | null; slot: number | null; name: string | null; paper?: boolean; paper_signed_on?: string | null; view_sha256: string | null; main_sha256?: string | null }[] | null;
  /** 0071: Dokument der Marke (PDF) statt Vertragstext; die Datei kommt über den Link, nie mit Speicherpfad */
  document_kind?: "template" | "brand_pdf";
  document?: { name: string; sha256: string; size: number | null; read_at: string | null } | null;
  /** 0485 (CP-025): eigene Ablehnung über diesen Link (nur in der Lesesicht) */
  declined?: { at: string; reason: string | null } | null;
}

type Failure = "expired" | "invalid" | "replaced" | "declined" | "void" | "read_expired" | "closed" | "ended" | "other";

export const T = {
  de: {
    skip: "Zum Inhalt", theme: "Hell / Dunkel",
    roleName: { agency: "Agentur", brand: "Marke", talent: "Creator" } as Record<ContractRole, string>,
    // Paket 2a (05.11.2026): Partei „agency“ eines Creator-Kontos (organizations.kind = creator) ist der Creator selbst
    creatorAccount: "Creator",
    guardianRole: "Erziehungsberechtigte Person",
    validUntil: "Link gültig bis", readUntil: "Lesbar bis",
    read: { sie: "Bitte lesen Sie das vollständige Dokument, bevor Sie unterschreiben.", du: "Lies das vollständige Dokument, bevor du unterschreibst." },
    fieldsTitle: { sie: "Ihre Angaben", du: "Deine Angaben" },
    fieldsText: "Diese Angaben stehen danach im Vertrag. Sobald eine Partei unterschrieben hat, sind sie fest.",
    fieldsLocked: "Eine Partei hat bereits unterschrieben; die Angaben sind jetzt fest.",
    saveFields: "Angaben speichern", saved: "Angaben gespeichert.",
    signature: "Unterschrift", name: "Vollständiger Name", position: "Funktion (z. B. Geschäftsführerin, Prokurist)",
    guardianHint: { sie: "Sie unterschreiben als gesetzliche Vertretung des Creators.", du: "Du unterschreibst als gesetzliche Vertretung." },
    consent: "Ich habe den Vertrag gelesen und stimme der Unterzeichnung zu.",
    sign: "Verbindlich unterschreiben", signing: "Wird unterschrieben …",
    protocol: "Protokolliert werden Zustimmung, Dokumentversion mit Prüfsumme, Zeitpunkt, IP-Adresse und Gerät.",
    decline: "Nicht unterschreiben und Grund nennen",
    declineText: { sie: "Der Vertrag wird damit angehalten; niemand kann ihn mehr unterschreiben. Die Agentur sieht Ihren Grund und meldet sich bei Ihnen.", du: "Der Vertrag wird damit angehalten; niemand kann ihn mehr unterschreiben. Die Agentur sieht deinen Grund und meldet sich bei dir." },
    reason: "Grund", doDecline: "Ablehnen",
    // Bedienbarkeits-Prüfung (Inhaber 02.11.2026): sagen, warum „Ablehnen“ noch nicht geht
    reasonHint: { sie: "Bitte nennen Sie einen Grund (mindestens 5 Zeichen).", du: "Bitte nenne einen Grund (mindestens 5 Zeichen)." },
    needName: { sie: "Bitte geben Sie Ihren vollständigen Namen ein.", du: "Bitte gib deinen vollständigen Namen ein." },
    needConsent: { sie: "Bitte bestätigen Sie, dass Sie den Vertrag gelesen haben.", du: "Bitte bestätige, dass du den Vertrag gelesen hast." },
    needFields: (list: string) => `Bitte zuerst ${list} eintragen.`,
    // Runde 2, R2-03/R2-05: dieselbe Zeichen- und Druckbarkeitsprüfung wie beim Versand
    badName: { sie: "Der Name enthält Zeichen, die nicht erlaubt sind oder die das PDF nicht drucken kann (etwa ein Emoji oder ein Steuerzeichen). Bitte tippen Sie ihn neu ein.", du: "Der Name enthält Zeichen, die nicht erlaubt sind oder die das PDF nicht drucken kann (etwa ein Emoji oder ein Steuerzeichen). Bitte tippe ihn neu ein." },
    badValue: (list: string) => `${list} enthält Zeichen, die nicht erlaubt sind oder die das PDF nicht drucken kann (etwa ein Emoji, chinesische oder hebräische Schrift). Bitte ersetzen.`,
    waitFill: "Der Vertrag ist noch nicht vollständig: Eine andere Partei muss noch Angaben eintragen. Unterschreiben geht danach.",
    minorGuardian: "Der Creator ist minderjährig: Diesen Vertrag muss auch seine gesetzliche Vertretung unterschreiben. Unterschreiben ist gesperrt, bis die Agentur eine neue Fassung mit den Eltern schickt.",
    thanks: "Danke, der Vertrag ist unterschrieben.",
    thanksText: { sie: "Sobald alle Parteien unterschrieben haben, bekommen Sie per E-Mail einen persönlichen Link zur unterschriebenen Fassung. Bis dahin können Sie den Vertrag über diesen Link lesen.", du: "Sobald alle Parteien unterschrieben haben, bekommst du per E-Mail einen persönlichen Link zur unterschriebenen Fassung. Bis dahin kannst du den Vertrag über diesen Link lesen." },
    showContract: "Vertrag ansehen",
    // CP-025: nach dem Ablehnen mit Agentur und dem Weg zum Vertrag, auch beim erneuten Öffnen des Links
    declined: { du: "Du hast den Vertrag abgelehnt", sie: "Sie haben den Vertrag abgelehnt" },
    declinedText: (agency: string, form: Form) => form === "du"
      ? `${agency || "Die Agentur"} ist informiert und sieht deinen Grund. Der Vertrag ist angehalten; die Agentur meldet sich bei dir, etwa mit einer neuen Fassung. Lesen kannst du ihn über diesen Link weiterhin.`
      : `${agency || "Die Agentur"} ist informiert und sieht Ihren Grund. Der Vertrag ist angehalten; die Agentur meldet sich bei Ihnen, etwa mit einer neuen Fassung. Lesen können Sie ihn über diesen Link weiterhin.`,
    stopped: "Der Vertrag ist angehalten: Eine Partei hat ihn abgelehnt. Unterschreiben geht erst wieder, wenn die Agentur eine neue Fassung schickt oder ihn wieder freigibt.",
    paused: "Die Kooperation zu diesem Vertrag wurde storniert. Unterschreiben ist gesperrt, bis die Agentur entscheidet.",
    readTitle: { completed: "Unterschriebene Fassung", open: "Ihre Unterschrift ist erfasst", void: "Aufgehobener Vertrag" },
    readText: { completed: { du: "Der Vertrag ist von allen Parteien unterschrieben. Sichere ihn als PDF für deine Unterlagen.", sie: "Der Vertrag ist von allen Parteien unterschrieben. Sichern Sie ihn als PDF für Ihre Unterlagen." }, open: { du: "Die anderen Parteien unterschreiben noch. Du bekommst die fertige Fassung per E-Mail.", sie: "Die anderen Parteien unterschreiben noch. Sie bekommen die fertige Fassung per E-Mail." }, void: { du: "", sie: "" } },
    print: "Drucken", pdf: "PDF herunterladen",
    fact: { signer: "Unterschrieben von", at: "Zeitpunkt", status: "Stand", agency: "Versendet von", waiting: "Wartet auf die übrigen Unterschriften", done: (d: string) => `Vollständig unterschrieben am ${d}`, cancelled: "Aufgehoben",
      declinedBy: "Abgelehnt von", declinedAt: "Abgelehnt am", reason: "Grund", stopped: "Angehalten" },
    side: {
      secure: "Elektronische Signatur mit Protokoll", contract: "Vertrag", parties: "Parteien", number: "Nummer", sent: "Versendet am", status: "Stand",
      st: { sign: "Wartet auf Ihre Unterschrift", signDu: "Wartet auf deine Unterschrift", open: "In Unterzeichnung", completed: "Vollständig unterschrieben", void: "Aufgehoben", stopped: "Angehalten", paused: "Gesperrt" },
      signed: (d: string) => `✓ unterschrieben am ${d}`, open: "offen", declined: "abgelehnt", guardian: "gesetzliche Vertretung", signNow: "Jetzt unterschreiben",
      missing: (wer: string) => `Es fehlt noch: ${wer}.`, unchanged: "Text unverändert",
    },
    protocolPdf: "Unterschriftenprotokoll (PDF)",
    docRead: (name: string) => `Ich habe das Dokument „${name}“ vollständig gelesen.`,
    needDocRead: { sie: "Bitte öffnen Sie das Dokument und bestätigen Sie, dass Sie es gelesen haben.", du: "Bitte öffne das Dokument und bestätige, dass du es gelesen hast." },
    docIntro: { sie: "Unterschrieben wird das Dokument der Marke. Bitte lesen Sie es vollständig; darunter steht die Unterschriftsseite, die Datei und Prüfwert nennt.", du: "Unterschrieben wird das Dokument der Marke. Bitte lies es vollständig; darunter steht die Unterschriftsseite, die Datei und Prüfwert nennt." },
    docSignature: "Einfache elektronische Signatur mit Protokoll (keine qualifizierte elektronische Signatur).",
    record: "Protokoll", atSend: "Prüfsumme beim Versand", atEnd: "Prüfsumme bei Abschluss", seen: "Ihre Fassung",
    events: { sent: "Versendet", document_read: "Dokument gelesen (bestätigt)", signed: "Unterschrieben", countersigned: "Gegengezeichnet", completed: "Vollständig unterschrieben", voided: "Aufgehoben", terminated: "Kündigung vermerkt" } as Record<string, string>,
    voided: (d: string, kind: string, reason: string | null, completed: boolean, form: Form) => `${kind} am ${d}${reason ? ` (${reason.trim().replace(/[.!]+$/, "")})` : ""}. ${completed
      ? "Bis dahin war der Vertrag mit den unten stehenden Unterschriften wirksam"
      : "Er war noch nicht von allen unterschrieben und ist nicht wirksam geworden"}; diese Fassung bleibt für ${form === "du" ? "deine" : "Ihre"} Unterlagen lesbar.`,
    voidKind: { withdrawn: "Zurückgezogen", error: "Als irrtümlich erfasst aufgehoben", mutual: "Einvernehmlich aufgehoben", superseded: "Durch eine neue Fassung ersetzt" } as Record<string, string>,
    terminated: (d: string) => `Der Vertrag ist gekündigt und endet am ${d}.`,
    needPosition: "Sie unterschreiben unter einem anderen Namen als eingeladen: Bitte geben Sie Ihre Funktion an (etwa Geschäftsführerin, Prokurist).",
    timeZoneNote: (tz: string, creator = false) => creator ? `Zeiten in der Zeitzone des Creators (${tz}).` : `Zeiten in der Zeitzone der Agentur (${tz}).`,
    errors: {
      expired: ["Dieser Link ist abgelaufen.", "Bitte fordern Sie einen neuen Link bei Ihrer Kontaktperson an."],
      replaced: ["Dieser Link wurde durch einen neueren ersetzt.", "Bitte verwenden Sie den Link aus der neuesten E-Mail zu diesem Vertrag."],
      invalid: ["Dieser Link ist ungültig.", "Bitte prüfen Sie, ob der Link vollständig ist, oder fordern Sie einen neuen an. Nach einem Neuversand gilt nur der neueste Link."],
      declined: ["Dieser Vertrag wurde über diesen Link abgelehnt.", "Die Agentur ist informiert und meldet sich, etwa mit einer neuen Fassung."],
      void: ["Dieser Vertrag wurde aufgehoben.", "Er gilt nicht mehr und kann nicht unterschrieben werden. Fragen beantwortet die Agentur."],
      read_expired: ["Dieser Lesezugang ist abgelaufen.", "Bitte fordern Sie bei der Agentur eine neue Ausfertigung an."],
      closed: ["Dieser Vertrag ist nicht mehr offen.", "Er wurde abgeschlossen oder aufgehoben."],
      // 0425 (S-7): Vertrag der Agentur mit CreatorOffice beendet
      ended: ["Dieser Vertrag kann nicht mehr unterschrieben werden.", "Bitte wenden Sie sich an Ihre Kontaktperson."],
      other: ["Das hat nicht geklappt.", "Bitte versuchen Sie es erneut."],
    } as Record<Failure, [string, string]>,
    loading: "Vertrag wird geladen …",
    imprint: "Impressum", privacy: "Datenschutzerklärung", represented: "Vertreten durch", vat: "USt-IdNr.",
    privacyContact: (who: string, mail: string) => `Fragen zur Verarbeitung Ihrer Daten beantwortet ${who}${mail ? ` unter ${mail}` : ""}.`,
    processor: (who: string) => `Diese Seite betreibt CreatorOffice im Auftrag von ${who}. Zum Nachweis des Vertragsschlusses verarbeitet werden Name, E-Mail, Zeitpunkt, IP-Adresse, Gerät und Unterschrift.`,
    productPrivacy: "Datenschutz bei CreatorOffice",
  },
  en: {
    skip: "Skip to content", theme: "Light / Dark",
    roleName: { agency: "Agency", brand: "Brand", talent: "Creator" } as Record<ContractRole, string>,
    creatorAccount: "Creator",
    guardianRole: "Legal guardian",
    validUntil: "Link valid until", readUntil: "Readable until",
    read: { sie: "Please read the entire document before signing.", du: "Please read the entire document before signing." },
    fieldsTitle: { sie: "Your details", du: "Your details" },
    fieldsText: "These details will appear in the contract. Once any party has signed, they can no longer be changed.",
    fieldsLocked: "A party has already signed; the details are now fixed.",
    saveFields: "Save details", saved: "Details saved.",
    signature: "Signature", name: "Full name", position: "Position (e.g. Managing Director, authorised officer)",
    guardianHint: { sie: "You are signing as the creator's legal guardian.", du: "You are signing as legal guardian." },
    consent: "I have read the contract and agree to sign it.",
    sign: "Sign with binding effect", signing: "Signing …",
    protocol: "Consent, document version with checksum, time, IP address and device are recorded.",
    decline: "Do not sign and give a reason",
    declineText: { sie: "The contract will be put on hold; nobody can sign it any more. The agency will see your reason and get back to you.", du: "The contract will be put on hold; nobody can sign it any more. The agency will see your reason and get back to you." },
    reason: "Reason", doDecline: "Decline",
    reasonHint: { sie: "Please give a reason (at least 5 characters).", du: "Please give a reason (at least 5 characters)." },
    needName: { sie: "Please enter your full name.", du: "Please enter your full name." },
    needConsent: { sie: "Please confirm that you have read the contract.", du: "Please confirm that you have read the contract." },
    needFields: (list: string) => `Please fill in ${list} first.`,
    badName: { sie: "The name contains characters that are not allowed or that the PDF cannot print (such as an emoji or a control character). Please type it again.", du: "The name contains characters that are not allowed or that the PDF cannot print (such as an emoji or a control character). Please type it again." },
    badValue: (list: string) => `${list} contains characters that are not allowed or that the PDF cannot print (such as an emoji, Chinese or Hebrew script). Please replace them.`,
    waitFill: "The contract is not complete yet: another party still has to enter details. You can sign afterwards.",
    minorGuardian: "The creator is a minor: this contract must also be signed by their legal guardian. Signing is locked until the agency sends a new version with the parents.",
    thanks: "Thank you, the contract is signed.",
    thanksText: { sie: "Once all parties have signed, you will receive a personal link to the signed version by e-mail. Until then you can read the contract via this link.", du: "Once all parties have signed, you will receive a personal link to the signed version by e-mail. Until then you can read the contract via this link." },
    showContract: "View contract",
    declined: { du: "You declined the contract", sie: "You declined the contract" },
    declinedText: (agency: string, _form: Form) => `${agency || "The agency"} has been informed and can see your reason. The contract is on hold; the agency will get back to you, for example with a new version. You can still read it via this link.`,
    stopped: "The contract is on hold: a party has declined it. It can only be signed again once the agency sends a new version or releases it again.",
    paused: "The collaboration for this contract has been cancelled. Signing is blocked until the agency decides.",
    readTitle: { completed: "Signed version", open: "Your signature has been recorded", void: "Cancelled contract" },
    readText: { completed: { du: "The contract has been signed by all parties. Keep a copy as a PDF for your records.", sie: "The contract has been signed by all parties. Keep a copy as a PDF for your records." }, open: { du: "The other parties are still signing. You will receive the final version by e-mail.", sie: "The other parties are still signing. You will receive the final version by e-mail." }, void: { du: "", sie: "" } },
    print: "Print", pdf: "Download PDF",
    fact: { signer: "Signed by", at: "Time", status: "Status", agency: "Sent by", waiting: "Waiting for the remaining signatures", done: (d: string) => `Fully signed on ${d}`, cancelled: "Cancelled",
      declinedBy: "Declined by", declinedAt: "Declined on", reason: "Reason", stopped: "On hold" },
    side: {
      secure: "Electronic signature with audit trail", contract: "Contract", parties: "Parties", number: "Number", sent: "Sent on", status: "Status",
      st: { sign: "Waiting for your signature", signDu: "Waiting for your signature", open: "Being signed", completed: "Fully signed", void: "Cancelled", stopped: "On hold", paused: "Locked" },
      signed: (d: string) => `✓ signed on ${d}`, open: "open", declined: "declined", guardian: "legal guardian", signNow: "Sign now",
      missing: (wer: string) => `Still missing: ${wer}.`, unchanged: "Text unchanged",
    },
    protocolPdf: "Signature record (PDF)",
    docRead: (name: string) => `I have read the document “${name}” in full.`,
    needDocRead: { sie: "Please open the document and confirm that you have read it.", du: "Please open the document and confirm that you have read it." },
    docIntro: { sie: "You are signing the brand's document. Please read it in full; below it is the signature page naming the file and its checksum.", du: "You are signing the brand's document. Please read it in full; below it is the signature page naming the file and its checksum." },
    docSignature: "Simple electronic signature with audit record (not a qualified electronic signature).",
    record: "Signature record", atSend: "Checksum at sending", atEnd: "Checksum at completion", seen: "Your version",
    events: { sent: "Sent", document_read: "Document read (confirmed)", signed: "Signed", countersigned: "Countersigned", completed: "Fully signed", voided: "Cancelled", terminated: "Termination recorded" } as Record<string, string>,
    voided: (d: string, kind: string, reason: string | null, completed: boolean, _form: Form) => `${kind} on ${d}${reason ? ` (${reason.trim().replace(/[.!]+$/, "")})` : ""}. ${completed
      ? "Until then the contract was in force with the signatures below"
      : "Not all parties had signed, so it never took effect"}; this version remains readable for your records.`,
    voidKind: { withdrawn: "Withdrawn", error: "Cancelled as recorded in error", mutual: "Cancelled by mutual agreement", superseded: "Replaced by a new version" } as Record<string, string>,
    terminated: (d: string) => `The contract has been terminated and ends on ${d}.`,
    needPosition: "You are signing under a different name than invited: please state your position (e.g. Managing Director, authorised officer).",
    timeZoneNote: (tz: string, creator = false) => creator ? `Times in the creator's time zone (${tz}).` : `Times in the agency's time zone (${tz}).`,
    errors: {
      expired: ["This link has expired.", "Please ask your contact person for a new link."],
      replaced: ["This link has been replaced by a newer one.", "Please use the link from the most recent e-mail about this contract."],
      invalid: ["This link is invalid.", "Please check that the link is complete or ask for a new one. After a new link has been sent, only the latest one works."],
      declined: ["This contract was declined via this link.", "The agency has been informed and will get back to you, for example with a new version."],
      void: ["This contract has been cancelled.", "It is no longer valid and cannot be signed. The agency is happy to answer questions."],
      read_expired: ["This read access has expired.", "Please ask the agency for a new copy."],
      closed: ["This contract is no longer open.", "It has been completed or cancelled."],
      ended: ["This contract can no longer be signed.", "Please contact your contact person."],
      other: ["Something went wrong.", "Please try again."],
    } as Record<Failure, [string, string]>,
    loading: "Loading contract …",
    imprint: "Legal notice", privacy: "Privacy policy", represented: "Represented by", vat: "VAT ID",
    privacyContact: (who: string, mail: string) => `Questions about the processing of your data are answered by ${who}${mail ? ` at ${mail}` : ""}.`,
    processor: (who: string) => `This page is operated by CreatorOffice on behalf of ${who}. To document the conclusion of the contract, name, e-mail, time, IP address, device and signature are processed.`,
    productPrivacy: "Privacy at CreatorOffice",
  },
} as const;

function hintOf(e: unknown): string {
  return e instanceof ApiError ? e.hint ?? "" : "";
}
/** Fehlerart aus dem Schlüssel der Datenbank (hint), sonst aus dem Text. */
export function failureOf(e: unknown): Failure | "used" | "use_view" | null {
  const hint = hintOf(e);
  const msg = e instanceof Error ? e.message : String(e);
  if (hint === "contract:ended") return "ended";
  if (hint === "link:replaced" || /durch einen neueren ersetzt/.test(msg)) return "replaced";
  if (hint === "link:expired" || /abgelaufen/.test(msg) && !/Lesezugang/.test(msg)) return "expired";
  if (hint === "link:used" || /bereits verwendet/.test(msg)) return "used";
  if (hint === "link:use_view") return "use_view";
  if (hint === "link:declined") return "declined";
  if (hint === "contract:void" || /aufgehoben/.test(msg)) return "void";
  if (hint === "link:read_expired" || /Lesezugang ist abgelaufen/.test(msg)) return "read_expired";
  if (hint === "link:invalid" || /ungültig|Ungültiger Link/.test(msg)) return "invalid";
  if (hint === "contract:closed" || /nicht mehr (offen|unterschrieben)/.test(msg)) return "closed";
  return null;
}

/** Paket 2a: Name der Partei; bei einem Creator-Konto heißt die Partei „agency“ „Creator“ statt „Agentur“ */
export function rollenName(lang: Lang, role: ContractRole, accountKind: string | null | undefined): string {
  const t = T[lang];
  return role === "agency" && accountKind === "creator" ? t.creatorAccount : t.roleName[role];
}

/** Datum (und Uhrzeit mit Zone) in der Zeitzone der Agentur: dieselbe Unterschrift zeigt überall dieselbe Zeit (Befund 28). */
function fmtDate(iso: string | null | undefined, lang: Lang, withTime = false, timeZone?: string | null): string {
  if (!iso) return "";
  const d = new Date(iso.length === 10 ? `${iso}T12:00:00Z` : iso);
  const tz = timeZone ?? undefined;
  return Number.isNaN(d.getTime()) ? "" : new Intl.DateTimeFormat(lang === "en" ? "en-GB" : "de-DE", withTime ? { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: tz, timeZoneName: "short" } : { dateStyle: "long", timeZone: tz }).format(d);
}

/**
 * Beschriftung der Steuernummer der Agentur im Fuß nach dem Land der Nummer (VERTRAEGE-12): Schweiz „UID/MWST-Nr.“,
 * Österreich „UID-Nr.“, sonst „USt-IdNr.“; englisch „VAT ID“.
 */
export function vatLabel(vatId: string | null | undefined, lang: Lang): string {
  const v = (vatId ?? "").trim().toUpperCase();
  if (lang === "en") return /^CHE/.test(v) ? "UID/VAT no." : "VAT ID";
  return /^CHE/.test(v) ? "UID/MWST-Nr." : /^ATU/.test(v) ? "UID-Nr." : T.de.vat;
}

/**
 * Rahmen der Signierseite in der Sprache des Unterzeichners, mit Impressum und Datenschutz der Agentur. Der Kopf trägt klein
 * das CreatorOffice-Logo; die Agentur steht mit ihrem Logo im Dokumentkopf des Vertrags (VertragPapier, PDF).
 */
function SignLayout({ lang, agency, title, signer, children }: { lang: Lang; agency: PublicView["agency"] | null; title: string; signer?: string | null; children: ReactNode }) {
  const t = T[lang];
  const who = agency?.legal_name ?? agency?.name ?? "";
  return (
    <>
      {/* Tab-Titel: Dokument bzw. Zustand vor der Agentur, damit mehrere offene Signierlinks unterscheidbar sind (SICHT-OEFFENTLICH-BETRIEB-33) */}
      <title>{`${title} · ${agency?.name || PLATFORM_NAME}`}</title>
      {/* Persönlicher Link: kein Suchindex; der Schlüssel in der Adresse wandert nie als Referrer mit (React hebt beides in den Kopf) */}
      <meta name="robots" content="noindex, nofollow, noarchive" />
      <meta name="referrer" content="no-referrer" />
      <a className="skip btn" href="#main">{t.skip}</a>
      <header className="webnav tk-noprint">
        <span className="brand co-brand"><CreatorOfficeLogo form="responsive" size="sm" /></span>
        {/* Inhaber 14.10.2026: Der Kopf sagt, worum es geht (Signatur mit Protokoll) und für wen der Link gilt */}
        <span className="tk-sign-kopf">
          <span className="tk-sign-sicher"><Schloss />{t.side.secure}</span>
          {signer && <span className="tk-sign-initialen" title={signer} aria-hidden="true">{initialen(signer)}</span>}
          <button type="button" onClick={toggleTheme}>{t.theme}</button>
        </span>
      </header>
      {children}
      {agency && (
        <footer className="footer tk-noprint tk-sign-footer" aria-label={t.imprint}>
          <div className="stack">
            <strong>{who}</strong>
            <small>{[agency.address, agency.representative ? `${t.represented} ${agency.representative}` : null, agency.register, agency.vat_id ? `${vatLabel(agency.vat_id, lang)} ${agency.vat_id}` : null, agency.email].filter(Boolean).join(" · ")}</small>
            <div className="row">
              {agency.imprint_url && <a href={agency.imprint_url} target="_blank" rel="noopener noreferrer">{t.imprint}</a>}
              {agency.privacy_url && <a href={agency.privacy_url} target="_blank" rel="noopener noreferrer">{t.privacy}</a>}
              <a href={`${WEBSITE_URL}/datenschutz.html`} target="_blank" rel="noopener noreferrer">{t.productPrivacy}</a>
            </div>
            {!agency.privacy_url && <small>{t.privacyContact(who, agency.privacy_email ?? agency.email ?? "")}</small>}
            <small>{t.processor(who)}</small>
          </div>
        </footer>
      )}
      <Toasts />
    </>
  );
}

export function Unterschreiben() {
  const [params] = useSearchParams();
  const signToken = (params.get("t") ?? "").trim();
  const readToken = (params.get("l") ?? "").trim();
  const token = readToken || signToken;
  const [view, setView] = useState<PublicView | null>(null);
  const [failure, setFailure] = useState<Failure | null>(token ? null : "invalid");
  const [values, setValues] = useState<Record<string, string>>({});
  const [sig, setSig] = useState<SignatureValue>({ name: "", dataUrl: null });
  const [position, setPosition] = useState("");
  const [consent, setConsent] = useState(false);
  const [docRead, setDocRead] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [done, setDone] = useState<"signed" | "declined" | null>(null);
  const [reason, setReason] = useState("");
  const [note, setNote] = useState<string | null>(null);
  const loaded = useRef(false);
  const browserLang: Lang = typeof navigator !== "undefined" && navigator.language?.toLowerCase().startsWith("en") ? "en" : "de";

  async function loadRead(tok: string) {
    const v = await api.rpcAsAnon<PublicView>("contract_public_view", { p_token: tok, p_mode: "read" });
    setView(v);
    setDone(null);
  }

  // Genau einmal laden: das Öffnen wird protokolliert (kein Neuladen in einer Abfrage)
  useEffect(() => {
    if (!token || loaded.current) return;
    loaded.current = true;
    void (async () => {
      try {
        if (readToken) return await loadRead(readToken);
        const v = await api.rpcAsAnon<PublicView>("contract_public_view", { p_token: signToken });
        setView(v);
        setValues(v.values ?? {});
        setSig({ name: v.signer.name ?? "", dataUrl: null });
        setPosition(v.signer.position ?? "");
      } catch (e) {
        const k = failureOf(e);
        // Nach der eigenen Unterschrift bleibt der Link ein Lesezugang (Befund 7), auch nach einer Aufhebung (Befund 6) und
        // nach der eigenen Ablehnung (CP-025, 0485)
        if (k === "used" || k === "void" || k === "declined") {
          try { await loadRead(signToken); } catch (e2) { setFailure((failureOf(e2) as Failure | null) ?? "other"); }
        } else setFailure((k as Failure | null) ?? "other");
      }
    })();
  }, [token, readToken, signToken]);

  const lang: Lang = view?.signer.ui_language ?? view?.language ?? browserLang;
  // CP-026: auch Bausteine, die t() nutzen (Darstellung des Vertrags, Seitenansicht), sprechen die Sprache dieser Seite
  setUiLang(lang);
  const t = T[lang];
  const rolle = (r: ContractRole) => rollenName(lang, r, view?.account_kind);
  // Anrede: Creator wie in den Einstellungen der Agentur (Standard du), Marke und gesetzliche Vertretung immer Sie (Befund 29)
  const form: Form = view?.role === "talent" && view.signer.capacity === "party" && view.address_form !== "sie" ? "du" : "sie";

  useEffect(() => {
    const before = document.documentElement.lang;
    document.documentElement.lang = lang;
    return () => { document.documentElement.lang = before; };
  }, [lang]);
  // 0071: Dokument der Marke über den persönlichen Link laden (nur mit passendem Prüfwert)
  const doc = view?.document ?? null;
  const docStand = useDokument(doc && view ? () => api.contractDocument(token, view.mode) : null, doc && view ? `${view.mode}:${doc.sha256}` : "", doc?.sha256 ?? null, lang);

  if (failure || !view) {
    const [title, text] = failure ? t.errors[failure] : [t.loading, ""];
    return (
      <SignLayout lang={lang} agency={view?.agency ?? null} title={title}>
        <main id="main" className="auth-shell stack" aria-busy={!failure}>
          {/* Karte wie die übrigen öffentlichen Zustandsseiten (Link abgelaufen, Kit/Insights ungültig) */}
          <section className="centerform panel stack">
            <h1>{title}</h1>
            {text && <p>{text}</p>}
          </section>
        </main>
      </SignLayout>
    );
  }

  const v = view;
  const fields = v.fillable ?? [];
  const missing = fields.filter((f) => f.required && !(values[f.key] ?? "").trim());
  const events = (v.protocol ?? []).map((pr, i) => ({ id: i, event: pr.event, at: pr.at, role: pr.role, slot: pr.slot, name: pr.name, paper: !!pr.paper,
    paperSignedOn: pr.paper_signed_on ?? null, view_sha256: pr.view_sha256, many: !!pr.role && v.signers.filter((x) => x.role === pr.role).length > 1 }));
  // Papierunterschrift: Datum auf dem Papier statt Erfassung als Unterschriftsdatum (VERTRAEGE-02)
  const onPaper = paperDates(events);
  const marks: SignatureMark[] = v.signers.map((s) => ({ role: s.role, slot: s.slot, capacity: s.capacity, signed: s.signed, signingName: s.signing_name,
    signingPosition: s.signing_position, signingAuthority: s.signing_authority, signedAt: s.signed_at, signatureData: s.signature_data, name: s.name,
    paper: s.signed && !!s.paper, paperSignedOn: onPaper.get(`${s.role}#${s.slot}`) ?? null }));
  const labelOf = (f: CustomVariableDef) => (lang === "en" ? f.label.en || f.label.de : f.label.de);
  const number = v.contract_number_label ?? (v.contract_number ? contractNumberLabel(v.contract_number) : null);
  const canSign = v.mode === "sign" && v.state === "open";
  const tz = v.timezone ?? "Europe/Berlin";
  const otherName = v.role === "brand" && sig.name.trim() !== "" && sig.name.trim().toLowerCase() !== (v.signer.name ?? "").trim().toLowerCase();
  const readKey = v.state === "completed" ? "completed" : v.state === "void" ? "void" : "open";
  async function downloadPdf() {
    await call("pdf", async () => {
      if (v.document) {
        const bytes = await unterschriftenprotokollPdf({ ...ausfertigungInput(), document: v.document });
        saveFile(bytes, protokollFileName(v.title, number, v.language));
        return true;
      }
      const bytes = await ausfertigungPdf({
        view: v.role === "agency" ? "agency" : v.role, language: v.language, title: v.title, numberLabel: number, agencyName: v.agency.name ?? "", timeZone: tz,
        logoPath: v.agency.logo_path, accent: v.agency.brand_color, layout: v.layout ?? null,
        status: v.status, sentAt: v.sent_at, completedAt: v.completed_at, voidedAt: v.voided_at ?? null, voidKind: v.void_kind ?? null, terminatedOn: v.terminated_on ?? null,
        html: v.html, annexHtml: v.annex_html,
        signers: v.signers.map((x) => ({ role: x.role, slot: x.slot, capacity: x.capacity, name: x.name, signed: x.signed, signing_name: x.signing_name,
          signing_position: x.signing_position, signing_authority: x.signing_authority, signed_at: x.signed_at, signature_data: x.signature_data, paper: !!x.paper })),
        events,
        documentSha: v.document_sha256 ?? v.part_sha256 ?? null, finalSha: v.final_sha256 ?? v.part_final_sha256 ?? null, viewSha: v.view_sha256,
      });
      saveFile(bytes, ausfertigungFileName(v.title, number, v.role === "agency" ? "agency" : v.role, v.language));
      return true;
    });
  }

  function ausfertigungInput() {
    return {
      view: (v.role === "agency" ? "agency" : v.role) as "agency" | "brand" | "talent", language: v.language, title: v.title, numberLabel: number, agencyName: v.agency.name ?? "", timeZone: tz,
      status: v.status, sentAt: v.sent_at, completedAt: v.completed_at, voidedAt: v.voided_at ?? null, voidKind: v.void_kind ?? null, terminatedOn: v.terminated_on ?? null,
      html: v.html, annexHtml: v.annex_html,
      signers: v.signers.map((x) => ({ role: x.role, slot: x.slot, capacity: x.capacity, name: x.name, signed: x.signed, signing_name: x.signing_name,
        signing_position: x.signing_position, signing_authority: x.signing_authority, signed_at: x.signed_at, signature_data: x.signature_data, paper: !!x.paper })),
      events,
      documentSha: v.document_sha256 ?? v.part_sha256 ?? null, finalSha: v.final_sha256 ?? v.part_final_sha256 ?? null, viewSha: v.view_sha256,
    };
  }

  async function call<R>(key: string, fn: () => Promise<R>): Promise<R | null> {
    setBusy(key);
    setProblem(null);
    try {
      return await fn();
    } catch (e) {
      const k = failureOf(e);
      const hint = hintOf(e);
      if (hint === "fill:chars" || hint === "fill:print") setProblem(t.badValue(fields.map((f) => `„${labelOf(f)}“`).join(", ")));
      else if (hint === "sign:name_chars") setProblem(t.badName[form]);
      else if (hint.startsWith("fill:")) setProblem(t.needFields(`„${labelOf(fields.find((f) => f.key === hint.slice(5)) ?? { key: hint.slice(5), label: { de: hint.slice(5), en: hint.slice(5) }, type: "text", required: true })}“`));
      else if (hint.startsWith("wait_fill:")) setProblem(t.waitFill);
      else if (hint === "contract:stopped") setView({ ...v, state: "stopped" });
      else if (hint === "deal:cancelled") setView({ ...v, state: "paused" });
      else if (hint === "contract:minor_guardian") setProblem(t.minorGuardian);
      else if (documentError(hint, lang)) setProblem(documentError(hint, lang));
      else if (k && k !== "used" && k !== "use_view") setFailure(k);
      else setProblem(lang === "en" ? t.errors.other[1] : e instanceof Error ? e.message : String(e));
      return null;
    } finally {
      setBusy(null);
    }
  }

  const badValues = () => fields.filter((f) => contractNameProblem(String(values[f.key] ?? "").trim()) !== null);

  async function saveFields() {
    const bad = badValues();
    if (bad.length) { setProblem(t.badValue(bad.map((f) => `„${labelOf(f)}“`).join(", "))); return; }
    const r = await call("fill", () => api.rpcAsAnon<{ ok: boolean }>("contract_public_fill", { p_token: signToken, p_values: values }));
    if (r) {
      setNote(t.saved);
      const nv = await call("view", () => api.rpcAsAnon<PublicView>("contract_public_view", { p_token: signToken }));
      if (nv) setView(nv);
    }
  }

  async function sign(e: FormEvent) {
    e.preventDefault();
    const p = sig.name.trim().length < 2 ? t.needName[form]
      : contractNameProblem(sig.name.trim()) ? t.badName[form]
      : badValues().length ? t.badValue(badValues().map((f) => `„${labelOf(f)}“`).join(", "))
      : otherName && !position.trim() ? t.needPosition
      : missing.length ? t.needFields(missing.map((f) => `„${labelOf(f)}“`).join(", "))
      : v.document && (!docRead || !docStand.verified) ? t.needDocRead[form]
      : !consent ? t.needConsent[form] : null;
    setProblem(p);
    if (p) return;
    const r = await call("sign", async () => {
      if (fields.length && !v.locked && JSON.stringify(values) !== JSON.stringify(v.values ?? {})) {
        await api.rpcAsAnon("contract_public_fill", { p_token: signToken, p_values: values });
      }
      // Dokument der Marke: das Lesen der gezeigten Datei (mit ihrem Prüfwert) steht vor der Unterschrift in der Beweiskette
      if (v.document) await confirmReadPublic(api, signToken, docStand.sha256!);
      return api.rpcAsAnon<{ status: string }>("contract_public_sign", {
        p_token: signToken, p_signing_name: sig.name.trim(), p_signature_data: sig.dataUrl, p_client: clientInfo(),
        p_signing_position: v.role === "brand" ? position.trim() || null : null,
      });
    });
    if (r) setDone("signed");
  }

  // Wer nach der eigenen Unterschrift noch fehlt (für die Bestätigung)
  const fehlend = v.signers.filter((x) => !x.signed && !x.declined && !(x.role === v.role && x.slot === v.signer.slot && x.capacity === v.signer.capacity))
    // Prüf-Chat Nachtest (03.11.2026), Befund 11: die gesetzliche Vertretung heißt so, nicht „Creator“
    .map((x) => `${x.capacity === "legal_guardian" ? t.guardianRole : rolle(x.role)}${x.name ? ` (${x.name})` : ""}`);
  if (done) {
    const titel = done === "signed" ? t.thanks : t.declined[form];
    return (
      <SignLayout lang={lang} agency={v.agency} signer={v.signer.name} title={titel}>
        <main id="main" className="auth-shell stack">
          <section className="centerform panel stack tk-sign-danke">
            {done === "signed" && <span className="tk-sign-zeichen umriss" aria-hidden="true">✓</span>}
            <h1>{titel}</h1>
            <p>{done === "signed" ? t.thanksText[form] : t.declinedText(v.agency.name ?? "", form)}</p>
            {done === "signed" && fehlend.length > 0 && <p className="tk-sign-fehlt">{t.side.missing(fehlend.join(", "))}</p>}
            {/* CP-025: auch nach dem Ablehnen führt der Link zum Vertrag (Lesesicht) */}
            <div className="row"><button type="button" className={done === "signed" ? "primary" : undefined} disabled={!!busy} onClick={() => void call("read", () => loadRead(signToken))}>{t.showContract}</button></div>
            {problem && <p className="tk-error" role="alert">{problem}</p>}
          </section>
        </main>
      </SignLayout>
    );
  }

  // Eckdaten der Standkarte aus dem Protokoll: die eigene Unterschrift (zuletzt) und der Abschluss
  const protokoll = v.protocol ?? [];
  const meineUnterschrift = [...protokoll].reverse().find((p) => (p.event === "signed" || p.event === "countersigned") && (p.name === v.signer.name || p.role === v.role)) ?? null;
  const abschluss = protokoll.find((p) => p.event === "completed") ?? null;

  // Inhaber 29.10.2026: je Unterschrift sichtbar, dass sie auf genau die versendete Fassung geleistet wurde (Prüfsumme der
  // gesehenen Fassung = Prüfsumme beim Versand), wie im TrueTalent-Portal; IP und Gerät bleiben im Protokoll-PDF
  const versandSumme = v.document_sha256 ?? v.part_sha256 ?? null;
  const unveraendert = (x: PublicSigner) => protokoll.some((p) => (p.event === "signed" || p.event === "countersigned") && !p.paper
    && p.role === x.role && (p.slot ?? 1) === (x.slot ?? 1) && !!p.view_sha256
    // Unterschrift ohne Sicht auf den vertraulichen Teil (z. B. Creator im Portal): gesehen = Hauptteil (main_sha256, 0117)
    && (p.view_sha256 === versandSumme || (!!v.document_sha256 && p.view_sha256 === p.main_sha256)));
  const stand = v.state === "open" ? (v.mode === "sign" ? (form === "du" ? t.side.st.signDu : t.side.st.sign) : t.side.st.open) : t.side.st[v.state];
  // CP-025: eigene Ablehnung (Lesesicht nach „Ablehnen“): Stand, Datum und Grund statt „Ihre Unterschrift ist erfasst“
  const abgelehnt = v.mode === "read" && v.declined ? v.declined : null;
  return (
    <SignLayout lang={lang} agency={v.agency} signer={v.signer.name} title={v.title}>
      <main id="main" className="auth-shell stack tk-sign">
        <h1>{v.title}</h1>
        <p className="tk-noprint">{v.agency.name} → {v.signer.name}
          {v.mode === "sign" && v.expires_at ? ` · ${t.validUntil} ${fmtDate(v.expires_at, lang, false, tz)}` : ""}
          {v.mode === "read" && v.read_until ? ` · ${t.readUntil} ${fmtDate(v.read_until, lang, false, tz)}` : ""}</p>
        {v.state === "stopped" && !abgelehnt && <p className="notice" role="status">{t.stopped}</p>}
        {v.state === "paused" && <p className="notice" role="status">{t.paused}</p>}
        {v.state === "void" && v.voided_at && <p className="notice" role="status">{t.voided(fmtDate(v.voided_at, lang, false, tz), t.voidKind[v.void_kind ?? "mutual"] ?? t.readTitle.void, v.void_reason ?? null, !!v.completed_at, form)}</p>}
        {v.terminated_on && v.state !== "void" && <p className="notice" role="status">{t.terminated(fmtDate(v.terminated_on, lang, false, tz))}</p>}
        {v.mode === "read" && (
          // Inhaber 14.10.2026: Die Bestätigung nach dem Unterschreiben als ruhige Standkarte (Zeichen, Aussage, die Eckdaten des
          // Vorgangs, dann die Knöpfe) statt Überschrift und Fließtext
          <section className={`panel tk-sign-stand tk-noprint ${abgelehnt ? "void" : readKey}`}>
            <div className="tk-sign-stand-kopf">
              <span className="tk-sign-zeichen" aria-hidden="true">{readKey === "void" || abgelehnt ? "–" : "✓"}</span>
              <div>
                <h2>{abgelehnt && readKey !== "void" ? t.declined[form] : t.readTitle[readKey]}</h2>
                {abgelehnt && readKey !== "void" ? <p>{t.declinedText(v.agency.name ?? "", form)}</p> : t.readText[readKey][form] && <p>{t.readText[readKey][form]}</p>}
              </div>
            </div>
            <dl className="tk-sign-fakten">
              <div><dt>{t.fact.agency}</dt><dd>{v.agency.name}</dd></div>
              <div><dt>{abgelehnt ? t.fact.declinedBy : t.fact.signer}</dt><dd>{v.signer.name}</dd></div>
              {abgelehnt && <div><dt>{t.fact.declinedAt}</dt><dd>{fmtDate(abgelehnt.at, lang, true, tz)}</dd></div>}
              {abgelehnt?.reason && <div><dt>{t.fact.reason}</dt><dd>{abgelehnt.reason}</dd></div>}
              {meineUnterschrift && !abgelehnt && <div><dt>{t.fact.at}</dt><dd>{fmtDate(meineUnterschrift.at, lang, true, tz)}</dd></div>}
              <div><dt>{t.fact.status}</dt><dd>{readKey === "void" ? t.fact.cancelled : abgelehnt ? t.fact.stopped : abschluss ? t.fact.done(fmtDate(abschluss.at, lang, false, tz)) : t.fact.waiting}</dd></div>
            </dl>
            <div className="row">
              {abgelehnt && readKey !== "void"
                ? <button type="button" className="primary" onClick={() => document.getElementById("vertragstext")?.scrollIntoView({ behavior: "smooth", block: "start" })}>{t.showContract}</button>
                : <button type="button" className="primary" disabled={!!busy} onClick={() => void downloadPdf()}>{v.document ? t.protocolPdf : t.pdf}</button>}
              <button type="button" onClick={() => window.print()}>{t.print}</button>
            </div>
            <small>{t.timeZoneNote(tz, v.account_kind === "creator")}</small>
          </section>
        )}
        <div className="tk-sign-raster">
        <aside className="tk-sign-seite tk-noprint" aria-label={t.side.contract}>
          <section className="panel tk-sign-karte">
            <div className="row between"><h2>{t.side.contract}</h2><span className={`pill${v.state === "completed" ? " success" : v.state === "open" ? " warning" : ""}`}>{stand}</span></div>
            <dl className="tk-sign-zeilen">
              <div><dt>{t.side.contract}</dt><dd>{v.title}</dd></div>
              {number && <div><dt>{t.side.number}</dt><dd>{number}</dd></div>}
              {v.sent_at && <div><dt>{t.side.sent}</dt><dd>{fmtDate(v.sent_at, lang, false, tz)}</dd></div>}
              {v.mode === "sign" && v.expires_at && <div><dt>{t.validUntil}</dt><dd>{fmtDate(v.expires_at, lang, false, tz)}</dd></div>}
              {v.mode === "read" && v.read_until && <div><dt>{t.readUntil}</dt><dd>{fmtDate(v.read_until, lang, false, tz)}</dd></div>}
            </dl>
          </section>
          <section className="panel tk-sign-karte">
            <h2>{t.side.parties}</h2>
            <ul className="tk-sign-parteien">
              {v.signers.map((x) => (
                <li key={`${x.role}-${x.slot}-${x.capacity}`}>
                  <span className="tk-sign-initialen" aria-hidden="true">{initialen(x.signing_name ?? x.name ?? rolle(x.role))}</span>
                  <span className="tk-sign-partei">
                    <strong>{x.signing_name ?? x.name ?? rolle(x.role)}</strong>
                    <small>{rolle(x.role)}{x.capacity === "legal_guardian" ? ` · ${t.side.guardian}` : ""}</small>
                    <small className={x.signed ? "ok" : undefined}>{x.declined ? t.side.declined : x.signed ? t.side.signed(x.signed_at ? fmtDate(x.signed_at, lang, false, tz) : "") : t.side.open}</small>
                    {x.signed && unveraendert(x) && <span className="tk-sign-geprueft">✓ {t.side.unchanged}</span>}
                  </span>
                </li>
              ))}
            </ul>
          </section>
          <section className="panel tk-sign-karte tk-sign-vertrauen">
            <h2><Schloss />{t.side.secure}</h2>
            <small>{t.protocol}</small>
          </section>
          {canSign && (
            <button type="button" className="primary tk-sign-los" onClick={() => document.getElementById("unterschrift")?.scrollIntoView({ behavior: "smooth", block: "start" })}>{t.side.signNow}</button>
          )}
        </aside>
        <div className="stack tk-sign-haupt" id="vertragstext">
          {doc && canSign && <p className="tk-noprint">{t.docIntro[form]}</p>}
          {doc && <div className="tk-noprint"><DokumentAnsicht stand={docStand} name={doc.name} size={doc.size} expected={doc.sha256} lang={lang} form={form} /></div>}
          <VertragPapier html={v.html} annexHtml={v.annex_html} marks={marks} defs={fields} lang={v.language} logoPath={v.agency.logo_path} accent={v.agency.brand_color}
            timeZone={tz} eyebrow={`${v.agency.name ?? ""}${number ? ` · ${number}` : ""}`}
            // Paket 2a: Unterschriftsfelder beim Creator-Konto mit „Creator“ statt „Agentur“ (in der Sprache des Vertrags)
            roleLabels={v.account_kind === "creator" ? { agency: rollenName(v.language, "agency", "creator"), brand: rollenName(v.language, "brand", null), talent: rollenName(v.language, "talent", null) } : undefined} />
          {v.mode === "read" && v.protocol && (
            <article className="paper tk-print-page tk-protocol" lang={lang}>
              <h2>{t.record}</h2>
              {(v.document_sha256 ?? v.part_sha256) && <p className="tk-mono">{t.atSend}: {v.document_sha256 ?? v.part_sha256}</p>}
              {(v.final_sha256 ?? v.part_final_sha256) && <p className="tk-mono">{t.atEnd}: {v.final_sha256 ?? v.part_final_sha256}</p>}
              {v.view_sha256 && v.view_sha256 !== (v.document_sha256 ?? v.part_sha256) && <p className="tk-mono">{t.seen}: {v.view_sha256}</p>}
              <div className="tablewrap">
                <table>
                  <caption className="sr-only">{t.record}</caption>
                  <tbody>
                    {v.protocol.map((p, i) => (
                      <tr key={i}><td>{fmtDate(p.at, lang, true, tz)}</td><td>{t.events[p.event] ?? p.event}</td><td>{[p.role ? rolle(p.role) : null, p.name].filter(Boolean).join(" · ")}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </article>
          )}
          {canSign && fields.length > 0 && (
            <section className="panel stack" aria-label={t.fieldsTitle[form]}>
              <h2>{t.fieldsTitle[form]}</h2>
              <p>{v.locked ? t.fieldsLocked : t.fieldsText}</p>
              {fields.map((f) => (
                <label key={f.key}>
                  {labelOf(f)}
                  <input value={values[f.key] ?? ""} maxLength={500} disabled={v.locked} onChange={(e) => setValues({ ...values, [f.key]: e.target.value })} />
                </label>
              ))}
              {!v.locked && <button type="button" disabled={!!busy} onClick={() => void saveFields()}>{t.saveFields}</button>}
              {note && <p role="status">{note}</p>}
            </section>
          )}
          {canSign && (
            <form id="unterschrift" className="panel stack tk-sign-formular" onSubmit={(e) => void sign(e)} noValidate aria-busy={!!busy}>
              <h2>{t.signature}</h2>
              <p>{t.read[form]}</p>
              {v.signer.capacity === "legal_guardian" && <p>{t.guardianHint[form]}</p>}
              <SignaturePad value={sig} onChange={setSig} nameLabel={t.name} lang={lang} formal={form === "sie"} upload />
              {v.role === "brand" && <label>{t.position}{otherName ? " *" : ""}<input value={position} maxLength={120} required={otherName} onChange={(e) => setPosition(e.target.value)} autoComplete="organization-title" /></label>}
              {otherName && !position.trim() && <small>{t.needPosition}</small>}
              {doc && <label className="row"><input type="checkbox" checked={docRead} disabled={!docStand.verified} onChange={(e) => setDocRead(e.target.checked)} required />{t.docRead(doc.name)}</label>}
              <label className="row"><input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} required />{t.consent}</label>
              {problem && <p className="tk-error" role="alert">{problem}</p>}
              <button className="primary" type="submit" disabled={!!busy || (!!doc && !docStand.verified)}>{busy === "sign" ? t.signing : t.sign}</button>
              <small>{t.protocol}{doc ? ` ${t.docSignature}` : ""}</small>
              <details className="stack">
                <summary>{t.decline}</summary>
                <p>{t.declineText[form]}</p>
                <label>{t.reason}<textarea value={reason} onChange={(e) => setReason(e.target.value)} minLength={5} maxLength={2000} aria-describedby="ablehnen-hinweis" /></label>
                {reason.trim().length < 5 && <small id="ablehnen-hinweis">{t.reasonHint[form]}</small>}
                <div className="row">
                  <button className="danger" type="button" disabled={!!busy || reason.trim().length < 5} onClick={async () => {
                    const r = await call("decline", () => api.rpcAsAnon("contract_public_decline", { p_token: signToken, p_reason: reason.trim(), p_user_agent: null }).then(() => true));
                    if (r) setDone("declined");
                  }}>{t.doDecline}</button>
                </div>
              </details>
            </form>
          )}
        </div>
        </div>
      </main>
    </SignLayout>
  );
}

/** Anfangsbuchstaben für den Kreis (höchstens zwei) */
function initialen(name: string): string {
  const teile = name.trim().split(/\s+/).filter(Boolean);
  return ((teile[0]?.[0] ?? "") + (teile.length > 1 ? teile[teile.length - 1]![0] : "")).toUpperCase() || "·";
}
function Schloss() {
  return (
    <svg className="tk-sign-schloss" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  );
}
